import CssBaseline from '@mui/material/CssBaseline';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from '@mui/material/styles';
import React, { ReactNode, createContext, useContext, useState } from 'react';

// Theme mode context
interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

// Theme configuration based on your JSON
const getTheme = (mode: 'light' | 'dark') => {
  const isLight = mode === 'light';

  return createTheme({
    cssVariables: true,
    palette: {
      mode,
      ...(isLight
        ? {
            // Light mode palette
            primary: {
              main: '#651fff',
              dark: '#5d19ff',
              light: '#7c4dff',
              contrastText: '#FFFFFF',
            },
            secondary: {
              main: '#9C27B0',
              dark: '#7B1FA2',
              contrastText: '#FFFFFF',
            },
            error: {
              main: '#D32F2F',
              dark: '#C62828',
              contrastText: '#FFFFFF',
            },
            warning: {
              main: '#EF6C00',
              dark: '#E65100',
              contrastText: '#FFFFFF',
            },
            info: {
              main: '#0288D1',
              dark: '#01579B',
              contrastText: '#FFFFFF',
            },
            success: {
              main: '#2E7D32',
              dark: '#1B5E20',
              contrastText: '#FFFFFF',
            },
            background: {
              default: '#F8FBFC',
              paper: '#FFFFFF',
            },
            text: {
              primary: 'rgba(0, 30, 42, 0.87)',
              secondary: 'rgba(0, 30, 42, 0.6)',
              disabled: 'rgba(0, 30, 42, 0.38)',
            },
            divider: 'rgba(19, 3, 38, 0.12)',
            action: {
              active: 'rgba(0, 30, 42, 0.56)',
              hover: 'rgba(0, 30, 42, 0.04)',
              selected: 'rgba(0, 30, 42, 0.08)',
              focus: 'rgba(0, 30, 42, 0.12)',
              disabled: 'rgba(0, 30, 42, 0.38)',
              disabledBackground: 'rgba(0, 0, 0, 0.12)',
            },
          }
        : {
            // Dark mode palette
            primary: {
              main: '#A572FD',
              dark: '#8E4EFC',
              light: '#D9C4FD',
              contrastText: 'rgba(19, 3, 38, 0.87)',
            },
            secondary: {
              main: '#CE93D8',
              dark: '#AB47BC',
              contrastText: 'rgba(19, 3, 38, 0.87)',
            },
            error: {
              main: '#F44336',
              dark: '#D32F2F',
              contrastText: '#FFFFFF',
            },
            warning: {
              main: '#FFA726',
              dark: '#F57C00',
              contrastText: 'rgba(19, 3, 38, 0.87)',
            },
            info: {
              main: '#29B6F6',
              dark: '#0288D1',
              contrastText: 'rgba(19, 3, 38, 0.87)',
            },
            success: {
              main: '#66BB6A',
              dark: '#388E3C',
              contrastText: 'rgba(19, 3, 38, 0.87)',
            },
            background: {
              default: '#110322',
              paper: '#1D112C',
            },
            text: {
              primary: '#FFFFFF',
              secondary: 'rgba(255, 255, 255, 0.7)',
              disabled: 'rgba(255, 255, 255, 0.5)',
            },
            divider: 'rgba(255, 255, 255, 0.12)',
            action: {
              active: 'rgba(255, 255, 255, 0.56)',
              hover: 'rgba(255, 255, 255, 0.04)',
              selected: 'rgba(255, 255, 255, 0.08)',
              disabled: 'rgba(255, 255, 255, 0.3)',
              disabledBackground: 'rgba(255, 255, 255, 0.12)',
            },
          }),
    },

    // Typography from JSON
    typography: {
      fontFamily: '"Space Grotesk", "Mulish", Helvetica, Arial, sans-serif',
      h1: {
        fontFamily: '"Space Grotesk", Helvetica, Arial, sans-serif',
      },
      h2: {
        fontFamily: '"Space Grotesk", Helvetica, Arial, sans-serif',
      },
      h3: {
        fontFamily: '"Space Grotesk", Helvetica, Arial, sans-serif',
        fontSize: '48px',
        fontWeight: 400,
        letterSpacing: '0px',
        lineHeight: '116.7%',
      },
      h4: {
        fontFamily: '"Space Grotesk", Helvetica, Arial, sans-serif',
        fontSize: '34px',
        fontWeight: 400,
        letterSpacing: '0.25px',
        lineHeight: '123.5%',
      },
      h5: {
        fontFamily: '"Space Grotesk", Helvetica, Arial, sans-serif',
      },
      h6: {
        fontFamily: '"Space Grotesk", Helvetica, Arial, sans-serif',
        fontSize: '20px',
        fontWeight: 500,
        letterSpacing: '0.15px',
        lineHeight: '150%',
      },
      body1: {
        fontFamily: '"Mulish", Helvetica, Arial, sans-serif',
        fontSize: '16px',
        fontWeight: 400,
        letterSpacing: '0.15px',
        lineHeight: '150%',
      },
      body2: {
        fontFamily: '"Mulish", Helvetica, Arial, sans-serif',
        fontSize: '14px',
        fontWeight: 400,
        letterSpacing: '0.17px',
        lineHeight: '143%',
      },
      subtitle1: {
        fontFamily: '"Space Grotesk", Helvetica, Arial, sans-serif',
      },
      subtitle2: {
        fontFamily: '"Space Grotesk", Helvetica, Arial, sans-serif',
        fontSize: '14px',
        fontWeight: 500,
        letterSpacing: '0.1px',
        lineHeight: '157%',
      },
      overline: {
        fontFamily: '"Mulish", Helvetica, Arial, sans-serif',
        fontSize: '12px',
        fontWeight: 400,
        letterSpacing: '1px',
        lineHeight: '266%',
      },
      caption: {
        fontFamily: '"Mulish", Helvetica, Arial, sans-serif',
        fontSize: '13px',
        fontWeight: 400,
        letterSpacing: '0.16px',
        lineHeight: '18px',
      },
    },

    // Shape from JSON
    shape: {
      borderRadius: 4,
    },

    // Shadows from JSON
    shadows: [
      'none',
      '0px 2px 1px -1px rgba(16, 30, 36, 0.20), 0px 1px 1px 0px rgba(16, 30, 36, 0.14), 0px 1px 3px 0px rgba(16, 30, 36, 0.12)',
      '0px 3px 1px -2px rgba(16, 30, 36, 0.20), 0px 2px 2px 0px rgba(16, 30, 36, 0.14), 0px 1px 5px 0px rgba(16, 30, 36, 0.12)',
      '0px 3px 3px -2px rgba(16, 30, 36, 0.20), 0px 3px 4px 0px rgba(16, 30, 36, 0.14), 0px 1px 8px 0px rgba(16, 30, 36, 0.12)',
      '0px 2px 4px -1px rgba(16, 30, 36, 0.20), 0px 4px 5px 0px rgba(16, 30, 36, 0.14), 0px 1px 10px 0px rgba(16, 30, 36, 0.12)',
      '0px 3px 5px -1px rgba(16, 30, 36, 0.20), 0px 5px 8px 0px rgba(16, 30, 36, 0.14), 0px 1px 14px 0px rgba(16, 30, 36, 0.12)',
      '0px 3px 5px -1px rgba(16, 30, 36, 0.20), 0px 6px 10px 0px rgba(16, 30, 36, 0.14), 0px 1px 18px 0px rgba(16, 30, 36, 0.12)',
      '0px 4px 5px -2px rgba(16, 30, 36, 0.20), 0px 7px 10px 1px rgba(16, 30, 36, 0.14), 0px 2px 16px 1px rgba(16, 30, 36, 0.12)',
      '0px 5px 5px -3px rgba(16, 30, 36, 0.20), 0px 8px 10px 1px rgba(16, 30, 36, 0.14), 0px 3px 14px 2px rgba(16, 30, 36, 0.12)',
      '0px 5px 6px -3px rgba(16, 30, 36, 0.20), 0px 9px 12px 1px rgba(16, 30, 36, 0.14), 0px 3px 16px 2px rgba(16, 30, 36, 0.12)',
      '0px 6px 6px -3px rgba(16, 30, 36, 0.20), 0px 10px 14px 1px rgba(16, 30, 36, 0.14), 0px 4px 18px 3px rgba(16, 30, 36, 0.12)',
      '0px 6px 7px -4px rgba(16, 30, 36, 0.20), 0px 11px 15px 1px rgba(16, 30, 36, 0.14), 0px 4px 20px 3px rgba(16, 30, 36, 0.12)',
      '0px 7px 8px -4px rgba(16, 30, 36, 0.20), 0px 12px 17px 2px rgba(16, 30, 36, 0.14), 0px 5px 22px 4px rgba(16, 30, 36, 0.12)',
      '0px 7px 8px -4px rgba(16, 30, 36, 0.20), 0px 13px 19px 2px rgba(16, 30, 36, 0.14), 0px 5px 24px 4px rgba(16, 30, 36, 0.12)',
      '0px 7px 9px -4px rgba(16, 30, 36, 0.20), 0px 14px 21px 2px rgba(16, 30, 36, 0.14), 0px 5px 26px 4px rgba(16, 30, 36, 0.12)',
      '0px 8px 9px -5px rgba(16, 30, 36, 0.20), 0px 15px 22px 2px rgba(16, 30, 36, 0.14), 0px 6px 28px 5px rgba(16, 30, 36, 0.12)',
      '0px 8px 10px -5px rgba(16, 30, 36, 0.20), 0px 16px 24px 2px rgba(16, 30, 36, 0.14), 0px 6px 30px 5px rgba(16, 30, 36, 0.12)',
      '0px 8px 11px -5px rgba(16, 30, 36, 0.20), 0px 17px 26px 2px rgba(16, 30, 36, 0.14), 0px 6px 32px 5px rgba(16, 30, 36, 0.12)',
      '0px 9px 11px -5px rgba(16, 30, 36, 0.20), 0px 18px 28px 2px rgba(16, 30, 36, 0.14), 0px 7px 34px 6px rgba(16, 30, 36, 0.12)',
      '0px 9px 12px -6px rgba(16, 30, 36, 0.20), 0px 19px 29px 2px rgba(16, 30, 36, 0.14), 0px 7px 36px 6px rgba(16, 30, 36, 0.12)',
      '0px 10px 13px -6px rgba(16, 30, 36, 0.20), 0px 20px 31px 3px rgba(16, 30, 36, 0.14), 0px 8px 38px 7px rgba(16, 30, 36, 0.12)',
      '0px 10px 13px -6px rgba(16, 30, 36, 0.20), 0px 21px 33px 3px rgba(16, 30, 36, 0.14), 0px 8px 40px 7px rgba(16, 30, 36, 0.12)',
      '0px 10px 14px -6px rgba(16, 30, 36, 0.20), 0px 22px 35px 3px rgba(16, 30, 36, 0.14), 0px 8px 42px 7px rgba(16, 30, 36, 0.12)',
      '0px 11px 14px -7px rgba(16, 30, 36, 0.20), 0px 23px 36px 3px rgba(16, 30, 36, 0.14), 0px 9px 44px 8px rgba(16, 30, 36, 0.12)',
      '0px 11px 15px -7px rgba(16, 30, 36, 0.20), 0px 24px 38px 3px rgba(16, 30, 36, 0.14), 0px 9px 46px 8px rgba(16, 30, 36, 0.12)',
    ],

    // Component overrides based on JSON
    components: {
      MuiCard: {
        // Remove the global defaultProps override that was affecting all Card components
        // defaultProps: {
        //   elevation: 0, // This was too broad and affected other components!
        // },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : theme.palette.background.paper,
            borderRadius: 8,
          }),
        },
      },

      MuiButton: {
        styleOverrides: {
          root: ({ theme, ownerState }) => {
            const {
              size = 'medium',
              variant = 'text',
              color = 'primary',
            } = ownerState;

            // Base styles
            let styles: any = {
              textTransform: 'none',
              fontFamily: '"Space Grotesk", Helvetica, Arial, sans-serif',
              fontWeight: 500,
              borderRadius: 8,
            };

            // Size-specific styles
            if (size === 'large') {
              styles = {
                ...styles,
                fontSize: '0.9375rem',
                lineHeight: '26px',
                letterSpacing: '0.46px',
                padding: '8px 22px',
              };
            } else if (size === 'medium') {
              styles = {
                ...styles,
                fontSize: '0.875rem',
                lineHeight: '24px',
                letterSpacing: '0.4px',
                padding: '6px 16px',
              };
            } else if (size === 'small') {
              styles = {
                ...styles,
                fontSize: '0.8125rem',
                lineHeight: '22px',
                letterSpacing: '0.46px',
                padding: '4px 10px',
              };
            }

            // Variant-specific styles
            if (variant === 'contained') {
              if (color === 'primary') {
                styles = {
                  ...styles,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  boxShadow: theme.shadows[6],
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: theme.shadows[4],
                  },
                  '&:disabled': {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                    boxShadow: 'none',
                  },
                };
              }
            } else if (variant === 'outlined') {
              if (color === 'primary') {
                styles = {
                  ...styles,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(179, 136, 255, 0.08)'
                        : 'rgba(0, 121, 107, 0.04)',
                  },
                  '&:disabled': {
                    color: theme.palette.action.disabled,
                    border: `1px solid ${theme.palette.action.disabledBackground}`,
                  },
                };
              }
            } else if (variant === 'text') {
              if (color === 'primary') {
                styles = {
                  ...styles,
                  color: theme.palette.primary.main,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(179, 136, 255, 0.08)'
                        : 'rgba(0, 121, 107, 0.04)',
                  },
                  '&:disabled': {
                    color: theme.palette.action.disabled,
                  },
                };
              }
            }

            return styles;
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: ({ theme }) => ({
            width: 58,
            height: 38,
            padding: 12,
            '& .MuiSwitch-switchBase': {
              padding: 9,
              '&.Mui-checked': {
                transform: 'translateX(20px)',
                '& .MuiSwitch-thumb': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.main
                      : '#FFFFFF',
                },
                '& + .MuiSwitch-track': {
                  backgroundColor: theme.palette.primary.main,
                  opacity: 0.5,
                },
              },
            },
            '& .MuiSwitch-thumb': {
              width: 20,
              height: 20,
              backgroundColor:
                theme.palette.mode === 'dark' ? '#E0E0E0' : '#001E2A',
              boxShadow: theme.shadows[1],
            },
            '& .MuiSwitch-track': {
              width: 34,
              height: 14,
              borderRadius: 100,
              backgroundColor:
                theme.palette.mode === 'dark' ? '#001E2A' : '#001E2A',
              opacity: 0.38,
            },
          }),
        },
      },

      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontFamily: '"Mulish", Helvetica, Arial, sans-serif',
            fontSize: '13px',
            fontWeight: 400,
            letterSpacing: '0.16px',
            lineHeight: '18px',
          }),
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.divider,
          }),
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor:
                theme.palette.mode === 'light'
                  ? 'rgba(0, 30, 42, 0.23)'
                  : 'rgba(255, 255, 255, 0.23)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor:
                theme.palette.mode === 'light'
                  ? 'rgba(0, 30, 42, 0.23)'
                  : 'rgba(255, 255, 255, 0.23)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          }),
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            border: 'none',
            borderRight: 'none',
            // Add shadow for depth and separation (based on Figma design)
            boxShadow:
              theme.palette.mode === 'light'
                ? '0px 1px 3px 0px rgba(16,30,36,0.12), 0px 1px 1px 0px rgba(16,30,36,0.14), 0px 2px 1px -1px rgba(16,30,36,0.2)'
                : '0px 1px 3px 0px rgba(0,0,0,0.15), 0px 1px 1px 0px rgba(0,0,0,0.20), 0px 2px 1px -1px rgba(0,0,0,0.25)',
          }),
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.Mui-selected': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(179, 136, 255, 0.16)'
                  : 'rgba(149, 117, 205, 0.12)',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(179, 136, 255, 0.24)'
                    : 'rgba(149, 117, 205, 0.16)',
              },
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }),
        },
      },

      MuiListItemIcon: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.action.active,
            minWidth: 40,
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          }),
        },
      },
    },
  });
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme from localStorage, fallback to 'dark'
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const savedTheme = localStorage.getItem('clips-theme-mode');
    return savedTheme === 'light' || savedTheme === 'dark'
      ? savedTheme
      : 'dark';
  });

  const toggleMode = () => {
    setMode((prev) => {
      const newMode = prev === 'light' ? 'dark' : 'light';
      // Persist theme preference to localStorage
      localStorage.setItem('clips-theme-mode', newMode);
      return newMode;
    });
  };

  const theme = getTheme(mode);

  return (
    <ThemeContext.Provider value={{ mode, toggleMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
